import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trophy, CheckCircle2, Trash2, Repeat, Wand2, Zap, ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatDate, formatTime } from "@/lib/format";

const DAYS = [["1", "Lun"], ["2", "Mar"], ["3", "Mié"], ["4", "Jue"], ["5", "Vie"], ["6", "Sáb"], ["7", "Dom"]];
const ZODIAC = ["Aries", "Tauro", "Géminis", "Cáncer", "Leo", "Virgo", "Libra", "Escorpio", "Sagitario", "Capricornio", "Acuario", "Piscis"];
const todayISO = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };

function GenerateTodayButton() {
  const qc = useQueryClient();
  const gen = useMutation({
    mutationFn: async () => (await api.post("/sorteos/generate-today")).data,
    onSuccess: (d) => {
      toast.success(`Sorteos de hoy: ${d.generated_count} creados, ${d.skipped_count} ya existían.`);
      qc.invalidateQueries({ queryKey: ["sorteos"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error al generar"),
  });
  return (
    <Button variant="outline" onClick={() => gen.mutate()} disabled={gen.isPending} data-testid="generate-today-button">
      <Wand2 size={16} className="mr-1.5"/>Generar Sorteos de Hoy
    </Button>
  );
}

// ---------------- Matriz Rápida de Resultados ----------------
function MatrizModal({ open, onOpenChange, lotMap }) {
  const qc = useQueryClient();
  const [date, setDate] = useState(todayISO());
  const [franja, setFranja] = useState("");
  const [values, setValues] = useState({}); // sorteo_id -> {number, sign}
  const inputRefs = useRef([]);

  const { data } = useQuery({
    queryKey: ["matriz-sorteos", date],
    queryFn: async () => {
      const first = (await api.get("/sorteos", { params: { date, limit: 200, page: 1 } })).data;
      let items = first.items || [];
      const pages = first.pages || 1;
      for (let p = 2; p <= pages; p++) {
        const r = (await api.get("/sorteos", { params: { date, limit: 200, page: p } })).data;
        items = items.concat(r.items || []);
      }
      return { items };
    },
    enabled: open,
  });
  const pendientes = useMemo(
    () => (data?.items || []).filter((s) => (s.status === "scheduled" || s.status === "open") && !(s.winning_numbers?.length)),
    [data],
  );
  const franjas = useMemo(() => {
    const m = new Map();
    pendientes.forEach((s) => { const k = formatTime(s.scheduled_at); if (!m.has(k)) m.set(k, s.scheduled_at); });
    return [...m.entries()].sort((a, b) => new Date(a[1]) - new Date(b[1])).map(([label]) => label);
  }, [pendientes]);
  const enFranja = useMemo(() => pendientes.filter((s) => formatTime(s.scheduled_at) === franja), [pendientes, franja]);

  const setVal = (id, patch) => setValues((v) => ({ ...v, [id]: { ...v[id], ...patch } }));
  const onKey = (e, idx) => {
    if (e.key === "Enter") { e.preventDefault(); const nxt = inputRefs.current[idx + 1]; if (nxt) nxt.focus(); }
  };

  const settle = useMutation({
    mutationFn: async () => {
      const items = enFranja.map((s) => {
        const lt = lotMap[s.lottery_type_id];
        const nlen = lt?.number_length || 0;
        const raw = (values[s.id]?.number || "").trim();
        const number = nlen ? raw.padStart(nlen, "0") : raw;
        const it = { sorteo_id: s.id, winning_numbers: [number] };
        if (lt?.has_zodiac_sign) it.winning_signs = [values[s.id]?.sign || ""];
        return it;
      }).filter((it) => it.winning_numbers[0]);
      if (items.length === 0) throw new Error("Ingresa al menos un número");
      return (await api.post("/sorteos/results/batch", { items })).data;
    },
    onSuccess: (d) => {
      const errs = d.errors?.length ? ` · ${d.errors.length} con error` : "";
      toast.success(`${d.settled_count} sorteos liquidados${errs}.`);
      if (d.errors?.length) d.errors.forEach((e) => toast.error(`${lotMap[enFranja.find((s) => s.id === e.sorteo_id)?.lottery_type_id]?.name || e.sorteo_id}: ${e.error}`));
      qc.invalidateQueries({ queryKey: ["sorteos"] });
      qc.invalidateQueries({ queryKey: ["matriz-sorteos", date] });
      qc.invalidateQueries({ queryKey: ["tickets"] });
      setValues({});
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message || "Error al liquidar"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-3xl" data-testid="matriz-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Zap size={18} className="text-primary"/>Matriz Rápida de Resultados</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">Fecha</Label><Input type="date" value={date} onChange={(e) => { setDate(e.target.value); setFranja(""); setValues({}); }} data-testid="matriz-date"/></div>
          <div className="space-y-1">
            <Label className="text-xs">Franja horaria</Label>
            <Select value={franja} onValueChange={(v) => { setFranja(v); setValues({}); }}>
              <SelectTrigger data-testid="matriz-franja"><SelectValue placeholder={franjas.length ? "Selecciona…" : "Sin sorteos pendientes"}/></SelectTrigger>
              <SelectContent>{franjas.map((f) => { const n = pendientes.filter((s) => formatTime(s.scheduled_at) === f).length; return <SelectItem key={f} value={f}>{f} · {n} {n === 1 ? "lotería" : "loterías"}</SelectItem>; })}</SelectContent>
            </Select>
          </div>
        </div>

        {franja && (
          <div className="mt-2 max-h-[45vh] overflow-y-auto space-y-2 pr-1" data-testid="matriz-grid">
            {enFranja.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No hay loterías pendientes en esta franja.</p>}
            {enFranja.map((s, idx) => {
              const lt = lotMap[s.lottery_type_id] || {};
              const nlen = lt.number_length || 3;
              return (
                <div key={s.id} className="flex items-center gap-2 rounded-lg border border-border p-2" data-testid={`matriz-row-${s.id}`}>
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: lt.color || "hsl(var(--primary))" }}/>
                  <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{lt.name || s.lottery_type_id}</div><div className="text-[11px] text-muted-foreground">{nlen} dígitos{lt.has_zodiac_sign ? " · zodiacal" : ""}</div></div>
                  <Input
                    ref={(el) => (inputRefs.current[idx] = el)}
                    className="w-24 font-mono text-center"
                    inputMode="numeric"
                    placeholder={"0".repeat(nlen)}
                    maxLength={nlen}
                    value={values[s.id]?.number || ""}
                    onChange={(e) => setVal(s.id, { number: e.target.value.replace(/\D/g, "").slice(0, nlen) })}
                    onKeyDown={(e) => onKey(e, idx)}
                    data-testid={`matriz-number-${s.id}`}
                  />
                  {lt.has_zodiac_sign && (
                    <Select value={values[s.id]?.sign || ""} onValueChange={(v) => setVal(s.id, { sign: v })}>
                      <SelectTrigger className="w-32" data-testid={`matriz-sign-${s.id}`}><SelectValue placeholder="Signo"/></SelectTrigger>
                      <SelectContent>{ZODIAC.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
          <Button onClick={() => settle.mutate()} disabled={settle.isPending || enFranja.length === 0} data-testid="matriz-settle-button">
            <CheckCircle2 size={16} className="mr-1.5"/>Liquidar Resultados de la Franja
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PlantillasTab({ lotteries, lotMap }) {
  const qc = useQueryClient();
  const [lottery, setLottery] = useState("");
  const [mode, setMode] = useState("interval");
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("19:00");
  const [stepMin, setStepMin] = useState(60);
  const [manualTimes, setManualTimes] = useState("");
  const [days, setDays] = useState(DAYS.map((d) => d[0]));

  const { data: plantillas = [] } = useQuery({ queryKey: ["plantillas"], queryFn: async () => (await api.get("/sorteos/plantillas")).data });

  const computedTimes = useMemo(() => {
    if (mode === "manual") return manualTimes.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
    const times = [];
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    let cur = sh * 60 + sm; const last = eh * 60 + em; const step = Math.max(5, Number(stepMin) || 60);
    while (cur <= last && times.length < 60) { times.push(`${String(Math.floor(cur / 60)).padStart(2, "0")}:${String(cur % 60).padStart(2, "0")}`); cur += step; }
    return times;
  }, [mode, manualTimes, start, end, stepMin]);

  const create = useMutation({
    mutationFn: async () => (await api.post("/sorteos/plantillas", { lottery_type_id: lottery, schedule_times: computedTimes, days_of_week: days.map(Number).sort() })).data,
    onSuccess: (d) => { toast.success(`Plantillas: ${d.created_count} creadas, ${d.skipped_count} ya existían.`); qc.invalidateQueries({ queryKey: ["plantillas"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });
  const toggle = useMutation({ mutationFn: async ({ id, active }) => (await api.patch(`/sorteos/plantillas/${id}`, { active })).data, onSuccess: () => qc.invalidateQueries({ queryKey: ["plantillas"] }), onError: (e) => toast.error(e?.response?.data?.detail || "Error") });
  const del = useMutation({ mutationFn: async (id) => (await api.delete(`/sorteos/plantillas/${id}`)).data, onSuccess: () => { toast.success("Plantilla eliminada"); qc.invalidateQueries({ queryKey: ["plantillas"] }); }, onError: (e) => toast.error(e?.response?.data?.detail || "Error") });

  const toggleDay = (d) => setDays((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]));
  const dayLabel = (arr) => (arr?.length === 7 ? "Todos" : (arr || []).map((n) => DAYS[n - 1]?.[1]).join(" "));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" data-testid="plantillas-tab">
      <Card className="p-4 bg-card border-border lg:col-span-2 space-y-4">
        <div className="font-display font-semibold flex items-center gap-2"><Repeat size={16} className="text-primary"/>Programar horarios recurrentes</div>
        <div className="space-y-2">
          <Label>Lotería</Label>
          <Select value={lottery} onValueChange={setLottery}>
            <SelectTrigger data-testid="plantilla-lottery"><SelectValue placeholder="Selecciona…"/></SelectTrigger>
            <SelectContent>{lotteries.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant={mode === "interval" ? "default" : "outline"} onClick={() => setMode("interval")} data-testid="plantilla-mode-interval">Por intervalo</Button>
          <Button size="sm" variant={mode === "manual" ? "default" : "outline"} onClick={() => setMode("manual")} data-testid="plantilla-mode-manual">Manual</Button>
        </div>
        {mode === "interval" ? (
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1"><Label className="text-xs">Desde</Label><Input type="time" value={start} onChange={(e) => setStart(e.target.value)} data-testid="plantilla-start"/></div>
            <div className="space-y-1"><Label className="text-xs">Hasta</Label><Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="plantilla-end"/></div>
            <div className="space-y-1"><Label className="text-xs">Cada (min)</Label><Input type="number" min="5" step="5" value={stepMin} onChange={(e) => setStepMin(e.target.value)} data-testid="plantilla-step"/></div>
          </div>
        ) : (
          <div className="space-y-1"><Label className="text-xs">Horarios (HH:MM, separados por coma)</Label><Input value={manualTimes} onChange={(e) => setManualTimes(e.target.value)} placeholder="09:00, 13:00, 16:00" data-testid="plantilla-manual-times"/></div>
        )}
        <div className="space-y-2">
          <Label className="text-xs">Días aplicables</Label>
          <div className="flex flex-wrap gap-1.5">
            {DAYS.map(([val, lbl]) => (
              <button key={val} onClick={() => toggleDay(val)} data-testid={`plantilla-day-${val}`} className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${days.includes(val) ? "border-primary/50 bg-primary/15 text-primary" : "border-border text-muted-foreground"}`}>{lbl}</button>
            ))}
          </div>
        </div>
        <div className="text-xs text-muted-foreground">Se crearán <span className="text-foreground font-medium">{computedTimes.length}</span> horarios: {computedTimes.slice(0, 8).join(", ")}{computedTimes.length > 8 ? "…" : ""}</div>
        <Button className="w-full" onClick={() => create.mutate()} disabled={create.isPending || !lottery || computedTimes.length === 0 || days.length === 0} data-testid="plantilla-create-submit"><Plus size={16} className="mr-1.5"/>Crear plantillas</Button>
      </Card>

      <Card className="bg-card border-border overflow-hidden lg:col-span-3" data-testid="admin-table-plantillas">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-xs uppercase tracking-wide text-muted-foreground text-left"><th className="px-4 py-3">Hora</th><th className="px-4 py-3">Lotería</th><th className="px-4 py-3">Días</th><th className="px-4 py-3">Activa</th><th className="px-4 py-3 text-right">Acciones</th></tr></thead>
            <tbody>
              {plantillas.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Sin plantillas. Crea horarios recurrentes a la izquierda.</td></tr>}
              {plantillas.map((p) => (
                <tr key={p.id} className="border-t border-border/60 hover:bg-secondary/50" data-testid={`plantilla-row-${p.id}`}>
                  <td className="px-4 py-3 font-mono text-sky-300">{p.schedule_time}</td>
                  <td className="px-4 py-3">{lotMap[p.lottery_type_id]?.name || "-"}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{dayLabel(p.days_of_week)}</td>
                  <td className="px-4 py-3"><Switch checked={p.active} onCheckedChange={(v) => toggle.mutate({ id: p.id, active: v })} data-testid={`plantilla-toggle-${p.id}`}/></td>
                  <td className="px-4 py-3 text-right"><Button size="icon" variant="ghost" className="text-rose-300" onClick={() => { if (window.confirm(`Eliminar plantilla ${p.schedule_time}?`)) del.mutate(p.id); }} data-testid={`plantilla-delete-${p.id}`}><Trash2 size={16}/></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function SorteosTab({ lotteries, lotMap }) {
  const qc = useQueryClient();
  const [openCreate, setOpenCreate] = useState(false);
  const [openResult, setOpenResult] = useState(null);
  const [openMatriz, setOpenMatriz] = useState(false);
  const [createForm, setCreateForm] = useState({ lottery_type_id: "", scheduled_at: "", label: "" });
  const [winStr, setWinStr] = useState("");
  const [winSignStr, setWinSignStr] = useState("");
  const [filters, setFilters] = useState({ date: todayISO(), status: "", origin: "", page: 1, limit: 50 });
  const set = (patch) => setFilters((f) => ({ ...f, ...patch, ...(patch.page ? {} : { page: 1 }) }));

  const params = { date: filters.date, page: filters.page, limit: filters.limit };
  if (filters.status) params.status_filter = filters.status;
  if (filters.origin) params.origin = filters.origin;

  const { data, isLoading } = useQuery({
    queryKey: ["sorteos", params],
    queryFn: async () => (await api.get("/sorteos", { params })).data,
    keepPreviousData: true,
  });
  const sorteos = data?.items || [];
  const totalPages = data?.pages || 0;

  const create = useMutation({
    mutationFn: async () => { const scheduled_at = new Date(createForm.scheduled_at).toISOString(); return (await api.post("/sorteos", { ...createForm, scheduled_at })).data; },
    onSuccess: () => { toast.success("Sorteo creado"); qc.invalidateQueries({ queryKey: ["sorteos"] }); setOpenCreate(false); setCreateForm({ lottery_type_id: "", scheduled_at: "", label: "" }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });
  const submitResult = useMutation({
    mutationFn: async () => {
      const winning_numbers = winStr.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
      const body = { winning_numbers };
      if (lotMap[openResult.lottery_type_id]?.has_zodiac_sign) body.winning_signs = winSignStr.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
      return (await api.post(`/sorteos/${openResult.id}/results`, body)).data;
    },
    onSuccess: (d) => { toast.success(`Resultados cargados. ${d.settlement?.winners || 0} ganadores.`); qc.invalidateQueries({ queryKey: ["sorteos"] }); qc.invalidateQueries({ queryKey: ["tickets"] }); qc.invalidateQueries({ queryKey: ["reports"] }); setOpenResult(null); setWinStr(""); setWinSignStr(""); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });
  const del = useMutation({ mutationFn: async (id) => (await api.delete(`/sorteos/${id}`)).data, onSuccess: () => { toast.success("Sorteo eliminado"); qc.invalidateQueries({ queryKey: ["sorteos"] }); }, onError: (e) => toast.error(e?.response?.data?.detail || "Error") });

  const now = new Date();
  const nowLocal = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

  return (
    <>
      {/* Barra de filtros + acciones */}
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <div className="space-y-1"><Label className="text-xs">Fecha</Label><Input type="date" value={filters.date} onChange={(e) => set({ date: e.target.value })} className="w-40" data-testid="sorteos-date-filter"/></div>
        <Button variant="outline" size="sm" onClick={() => set({ date: todayISO() })} data-testid="sorteos-today-button"><CalendarDays size={14} className="mr-1"/>Hoy</Button>
        <div className="space-y-1">
          <Label className="text-xs">Estado</Label>
          <Select value={filters.status || "all"} onValueChange={(v) => set({ status: v === "all" ? "" : v })}>
            <SelectTrigger className="w-36" data-testid="sorteos-status-filter"><SelectValue/></SelectTrigger>
            <SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="scheduled">Programado</SelectItem><SelectItem value="open">Abierto</SelectItem><SelectItem value="closed">Cerrado</SelectItem><SelectItem value="settled">Liquidado</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Origen</Label>
          <Select value={filters.origin || "all"} onValueChange={(v) => set({ origin: v === "all" ? "" : v })}>
            <SelectTrigger className="w-36" data-testid="sorteos-origin-filter"><SelectValue/></SelectTrigger>
            <SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="recurrente">Recurrente</SelectItem><SelectItem value="manual">Manual</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="flex-1"/>
        <Button onClick={() => setOpenMatriz(true)} className="bg-primary text-primary-foreground" data-testid="matriz-open-button"><Zap size={16} className="mr-1.5"/>Carga Rápida de Resultados</Button>
        <Button variant="outline" onClick={() => { setCreateForm({ lottery_type_id: lotteries[0]?.id || "", scheduled_at: nowLocal, label: "" }); setOpenCreate(true); }} data-testid="sorteo-create-button"><Plus size={16} className="mr-1.5"/>Programar sorteo</Button>
      </div>

      <Card className="bg-card border-border overflow-hidden" data-testid="admin-table-sorteos">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-xs uppercase tracking-wide text-muted-foreground text-left"><th className="px-4 py-3">Lotería</th><th className="px-4 py-3">Programado</th><th className="px-4 py-3">Origen</th><th className="px-4 py-3">Estado</th><th className="px-4 py-3">Ganadores</th><th className="px-4 py-3 text-right">Acciones</th></tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={6} className="px-4 py-6 text-muted-foreground">Cargando…</td></tr>}
              {!isLoading && sorteos.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Sin sorteos para esta fecha/filtros.</td></tr>}
              {sorteos.map((s) => (
                <tr key={s.id} className="border-t border-border/60 hover:bg-secondary/50">
                  <td className="px-4 py-3"><div className="font-medium">{lotMap[s.lottery_type_id]?.name || s.lottery_type_id}</div><div className="text-xs text-muted-foreground">{s.label}</div></td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatDate(s.scheduled_at)}</td>
                  <td className="px-4 py-3">{s.plantilla_id ? <Badge className="bg-primary/15 text-primary border border-primary/30 text-[11px]"><Repeat size={11} className="mr-1"/>Recurrente</Badge> : <span className="text-xs text-muted-foreground">Manual</span>}</td>
                  <td className="px-4 py-3"><SorteoStatusBadge status={s.status}/></td>
                  <td className="px-4 py-3">{s.winning_numbers?.length > 0 ? (<div className="flex gap-1 flex-wrap">{s.winning_numbers.map((n, i) => (<span key={i} className="px-2 py-0.5 rounded-md border border-amber-500/40 text-amber-300 bg-amber-500/10 font-mono text-xs">{n}{s.winning_signs?.[i] ? ` · ${s.winning_signs[i]}` : ""}</span>))}</div>) : <span className="text-muted-foreground text-xs">Sin cargar</span>}</td>
                  <td className="px-4 py-3 text-right"><div className="inline-flex gap-1">
                    {s.status !== "settled" && <Button size="sm" variant="outline" onClick={() => setOpenResult(s)} data-testid={`sorteo-result-${s.id}`}><Trophy size={14} className="mr-1.5"/> Resultados</Button>}
                    {s.status === "settled" && <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"><CheckCircle2 size={12} className="mr-1"/>Liquidado</Badge>}
                    {s.status === "scheduled" && <Button size="icon" variant="ghost" className="text-rose-300" onClick={() => { if (window.confirm("Eliminar sorteo?")) del.mutate(s.id); }}><Trash2 size={16}/></Button>}
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Paginación */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border text-sm">
          <div className="text-muted-foreground" data-testid="sorteos-pagination-info">{data?.total ?? 0} sorteos · página {data?.page ?? 1} de {totalPages || 1}</div>
          <div className="flex items-center gap-2">
            <Select value={String(filters.limit)} onValueChange={(v) => set({ limit: Number(v) })}>
              <SelectTrigger className="w-24 h-8" data-testid="sorteos-limit"><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="25">25</SelectItem><SelectItem value="50">50</SelectItem><SelectItem value="100">100</SelectItem><SelectItem value="200">200</SelectItem></SelectContent>
            </Select>
            <Button size="icon" variant="outline" disabled={filters.page <= 1} onClick={() => set({ page: filters.page - 1 })} data-testid="sorteos-prev"><ChevronLeft size={16}/></Button>
            <Button size="icon" variant="outline" disabled={filters.page >= totalPages} onClick={() => set({ page: filters.page + 1 })} data-testid="sorteos-next"><ChevronRight size={16}/></Button>
          </div>
        </div>
      </Card>

      <MatrizModal open={openMatriz} onOpenChange={setOpenMatriz} lotMap={lotMap}/>

      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle>Programar sorteo</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Lotería</Label>
              <Select value={createForm.lottery_type_id} onValueChange={(v) => setCreateForm({...createForm, lottery_type_id: v})}>
                <SelectTrigger><SelectValue placeholder="Selecciona…"/></SelectTrigger>
                <SelectContent>{lotteries.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Fecha y hora</Label><Input type="datetime-local" value={createForm.scheduled_at} onChange={(e) => setCreateForm({...createForm, scheduled_at: e.target.value})}/></div>
            <div className="space-y-2"><Label>Etiqueta (opcional)</Label><Input value={createForm.label} onChange={(e) => setCreateForm({...createForm, label: e.target.value})} placeholder="Ej: Triple A 01:00 PM"/></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpenCreate(false)}>Cancelar</Button><Button onClick={() => create.mutate()} disabled={create.isPending || !createForm.lottery_type_id || !createForm.scheduled_at} data-testid="sorteo-create-submit">Programar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!openResult} onOpenChange={(v) => { if (!v) setOpenResult(null); }}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle>Cargar resultados</DialogTitle></DialogHeader>
          {openResult && (
            <div className="space-y-3">
              <div className="rounded-lg border border-border p-3"><div className="text-sm font-medium">{lotMap[openResult.lottery_type_id]?.name}</div><div className="text-xs text-muted-foreground">Programado {formatDate(openResult.scheduled_at)}</div></div>
              <div className="space-y-2"><Label>Números ganadores</Label><Input value={winStr} onChange={(e) => setWinStr(e.target.value)} placeholder="Ej: 345 o 12,07,21" data-testid="sorteo-result-input"/><p className="text-xs text-muted-foreground">Separa múltiples números con coma o espacio.</p></div>
              {lotMap[openResult.lottery_type_id]?.has_zodiac_sign && (
                <div className="space-y-2"><Label>Signos ganadores</Label><Input value={winSignStr} onChange={(e) => setWinSignStr(e.target.value)} placeholder="Ej: Leo o Leo, Virgo, Aries" data-testid="sorteo-result-sign-input"/><p className="text-xs text-muted-foreground">Un signo por cada número, en el mismo orden.</p></div>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setOpenResult(null)}>Cancelar</Button><Button onClick={() => submitResult.mutate()} disabled={submitResult.isPending || !winStr.trim()} data-testid="sorteo-result-submit">Cargar y liquidar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function SorteosPage() {
  const { data: lotteries = [] } = useQuery({ queryKey: ["lottery-types"], queryFn: async () => (await api.get("/lottery-types")).data });
  const lotMap = useMemo(() => Object.fromEntries(lotteries.map((l) => [l.id, l])), [lotteries]);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Sorteos & Resultados</h1>
          <p className="text-sm text-muted-foreground">Programa sorteos puntuales o recurrentes y carga números ganadores.</p>
        </div>
        <GenerateTodayButton/>
      </div>
      <Tabs defaultValue="sorteos">
        <TabsList>
          <TabsTrigger value="sorteos" data-testid="sorteos-tab-sorteos">Sorteos</TabsTrigger>
          <TabsTrigger value="plantillas" data-testid="sorteos-tab-plantillas">Plantillas recurrentes</TabsTrigger>
        </TabsList>
        <TabsContent value="sorteos" className="pt-3"><SorteosTab lotteries={lotteries} lotMap={lotMap}/></TabsContent>
        <TabsContent value="plantillas" className="pt-3"><PlantillasTab lotteries={lotteries} lotMap={lotMap}/></TabsContent>
      </Tabs>
    </div>
  );
}

function SorteoStatusBadge({ status }) {
  const map = {
    scheduled: ["Programado", "border-sky-500/30 text-sky-300 bg-sky-500/10"],
    open: ["Abierto", "border-primary/40 text-primary bg-primary/10"],
    closed: ["Cerrado", "border-amber-500/30 text-amber-300 bg-amber-500/10"],
    settled: ["Liquidado", "border-emerald-500/30 text-emerald-300 bg-emerald-500/10"],
  };
  const [label, cls] = map[status] || [status, ""];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${cls}`}>{label}</span>;
}
